import { Fragment, type ReactNode } from 'react';

import type { IHelpHighlight } from '@/hooks/queries/help-centre/help-centre.types';

export interface IHighlightPart {
	text: string;
	match: boolean;
}

/**
 * Split a search snippet into plain and matched runs from the server's `highlights` offsets.
 *
 * The snippet is PLAIN TEXT (the server never sends markup) and is rendered as React text nodes —
 * there is no `dangerouslySetInnerHTML` anywhere in the Help Centre, so a guide body containing
 * `<img onerror=…>` can only ever print itself. Overlapping or out-of-range offsets are dropped
 * rather than trusted.
 */
export const highlightParts = (snippet: string, highlights: readonly IHelpHighlight[]): IHighlightPart[] => {
	const parts: IHighlightPart[] = [];
	let cursor = 0;
	[...highlights]
		.sort((a, b) => a.start - b.start)
		.forEach(({ start, length }) => {
			if (start < cursor || length <= 0 || start + length > snippet.length) return;
			if (start > cursor) parts.push({ text: snippet.slice(cursor, start), match: false });
			parts.push({ text: snippet.slice(start, start + length), match: true });
			cursor = start + length;
		});
	if (cursor < snippet.length) parts.push({ text: snippet.slice(cursor), match: false });
	return parts;
};

export const Highlight = ({ snippet, highlights }: { snippet: string; highlights: readonly IHelpHighlight[] }): ReactNode =>
	highlightParts(snippet, highlights).map((part, i) =>
		part.match ? <mark key={i}>{part.text}</mark> : <Fragment key={i}>{part.text}</Fragment>
	);
